/**
 * 导航结构。按用户心智的链路排，不按后端模块排。
 *
 *   计划与布置 → 执行与记载 → 采收与考种 → 决策与接续
 *
 * 一等页面（主路径）占第一层，基础管理收进第二层。
 */
import type { ModulePermission } from '@/services/authApi'

export interface NavItem {
  key: string
  path: string
  label: string
  /** 一句话说明这一页回答什么问题 */
  hint: string
  requiredPermission?: ModulePermission
  adminOnly?: boolean
}

export interface NavSection {
  key: string
  label: string
  items: NavItem[]
}

/** 工作台常驻在流程分组上方，负责看状态与找下一步。 */
export const WORKSPACE_NAV: NavSection = {
  key: 'workspace-section',
  label: '本季总览',
  items: [
    {
      key: 'workspace',
      path: '/season',
      label: '工作台',
      hint: '这一季现在怎样、哪里卡住、下一步做什么',
      requiredPermission: 'breeding-plan',
    },
  ],
}

export const PLANNING_NAV: NavSection = {
  key: 'planning',
  label: '1 计划与布置',
  items: [
    {
      key: 'external-materials',
      path: '/season/external-materials',
      label: '外部材料',
      hint: '登记、审核并入库外部引进材料',
      requiredPermission: 'breeding-plan',
    },
    {
      key: 'layout',
      path: '/season/layout',
      label: '布置台',
      hint: '这一季的行怎么排、种什么',
      requiredPermission: 'breeding-plan',
    },
  ],
}

export const EXECUTION_NAV: NavSection = {
  key: 'execution',
  label: '2 执行与记载',
  items: [
    {
      key: 'tasks',
      path: '/season/tasks',
      label: '田间任务',
      hint: '田里谁做什么、做到哪一步',
      requiredPermission: 'breeding-plan',
    },
    {
      key: 'voice-notes',
      path: '/season/voice-notes',
      label: '语音记载',
      hint: '田里录的音转成一批观察记录',
      requiredPermission: 'breeding-plan',
    },
  ],
}

export const HARVEST_NAV: NavSection = {
  key: 'harvest',
  label: '3 采收与考种',
  items: [
    {
      key: 'batches',
      path: '/season/batches',
      label: '采收记录',
      hint: '按种植行查看单头与混收的采收性状',
      requiredPermission: 'breeding-plan',
    },
    {
      key: 'seed-tests',
      path: '/season/seed-tests',
      label: '考种记录',
      hint: '一次打开一个采收批次，连续录入、复测并审核',
      requiredPermission: 'breeding-plan',
    },
  ],
}

export const DECISION_NAV: NavSection = {
  key: 'decision-flow',
  label: '4 决策与接续',
  items: [
    {
      key: 'confirm',
      path: '/season/confirm',
      label: '待确认',
      hint: '计划书里的上代行号到底指谁',
      requiredPermission: 'breeding-plan',
    },
    {
      key: 'decision',
      path: '/season/decision',
      label: '留淘决策',
      hint: '这批材料留谁、淘谁、进下一轮',
      requiredPermission: 'breeding-plan',
    },
    {
      key: 'allocations',
      path: '/season/allocations',
      label: '下季用种',
      hint: '把一批材料分到科研、繁殖、品比和多点计划',
      requiredPermission: 'breeding-plan',
    },
  ],
}

/** 基础管理：需要时进入，不占主路径。 */
export const BASE_NAV: NavSection = {
  key: 'base',
  label: '基础管理',
  items: [
    {
      key: 'organization',
      path: '/base/organization',
      label: '人员与部门',
      hint: '总公司、分公司、部门、组与员工账号',
      adminOnly: true,
    },
    {
      key: 'field',
      path: '/base/field',
      label: '站点地块',
      hint: '实验站、地块边界、功能区与计划书占用关系',
      requiredPermission: 'breeding-plan',
    },
    {
      key: 'germplasm',
      path: '/base/germplasm',
      label: '种质库',
      hint: '种质档案与固有性状',
      requiredPermission: 'germplasm',
    },
  ],
}

export const FLOW_SECTIONS: NavSection[] = [
  PLANNING_NAV,
  EXECUTION_NAV,
  HARVEST_NAV,
  DECISION_NAV,
]

export const ALL_SECTIONS: NavSection[] = [WORKSPACE_NAV, ...FLOW_SECTIONS, BASE_NAV]

export function findNavSection(pathname: string): NavSection | undefined {
  const activeItem = findNavItem(pathname)
  if (!activeItem) return undefined
  return ALL_SECTIONS.find((section) => section.items.some((item) => item.key === activeItem.key))
}

export function findNavItem(pathname: string): NavItem | undefined {
  const items = ALL_SECTIONS.flatMap((section) => section.items)
  // 最长前缀匹配，让 /season/layout 命中布置台而不是工作台
  return items
    .filter((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]
}
